import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../Components/AuditOperationDetail.css';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import UsedTools from '../Components/UsedTools';
import { LlmLogApiProvider, useLlmLogApi } from '../Services/LlmLogApiProvider';
import { formatDateUTC } from '../utils/formatDate';

const messageStyle = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  margin: 0,
};

const formatTokens = (value) => (value != null ? Number(value).toLocaleString() : '-');

function LlmTraceBlock({ title, messages }) {
  return (
    <div className="radius-yes p-2 bg-yes col-12">
      <h2 className="h5-title">{title}</h2>
      <div className="radius-yes bg-yes w-100">
        {!messages?.length ? (
          <p>No {title.toLowerCase()} captured for this request.</p>
        ) : (
          messages.map((message, index) => (
            <div className="radius-yes bg-yes mb-2 p-2" key={index}>
              <p style={messageStyle}>{message}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function LlmRequestDetail() {
  const { llmRequestId } = useParams();
  const { getLlmLogById } = useLlmLogApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [llmLog, setLlmLog] = useState(null);

  useEffect(() => {
    const fetchLlmLog = async () => {
      setLoading(true);
      try {
        const response = await getLlmLogById(llmRequestId);
        if (response) {
          setLlmLog(response);
        }
      } catch (error) {
        console.error('Error fetching llm log:', error);
        setError('Failed to load LLM request');
      } finally {
        setLoading(false);
      }
    };

    fetchLlmLog();
  }, [llmRequestId, getLlmLogById]);

  return (
    <div className="container">
      <Breadcrumbs />

      {loading ? (
        <PageLoader />
      ) : error ? (
        <div className="radius-yes p-2 bg-yes col-12">Error: {error}</div>
      ) : (
        <div className="row d-flex gap">
          <div className="radius-yes p-2 bg-yes col-12">
            <h2 className="h5-title">LLM Request</h2>
            <div className="radius-yes bg-yes">
              <div className="detail-item">
                <label>
                  <strong>Request Id:</strong>
                </label>
                <span>{llmLog?.id || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Model:</strong>
                </label>
                <span>{llmLog?.model_name || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Usage Type:</strong>
                </label>
                <span>{llmLog?.usage_type || ''}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Reasoning:</strong>
                </label>
                <span>{llmLog?.reasoning || ''}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Created At:</strong>
                </label>
                <span>{llmLog?.created_at ? formatDateUTC(llmLog.created_at) : '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Duration:</strong>
                </label>
                <span>{llmLog?.duration != null ? `${llmLog.duration.toFixed(2)} s` : '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Total Tokens:</strong>
                </label>
                <span>{formatTokens(llmLog?.total_tokens)}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Input Tokens:</strong>
                </label>
                <span>{formatTokens(llmLog?.input_tokens)}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Output Tokens:</strong>
                </label>
                <span>{formatTokens(llmLog?.output_tokens)}</span>
              </div>
            </div>
          </div>

          <LlmTraceBlock title="Input" messages={llmLog?.input} />
          <UsedTools tools={llmLog?.tools} />
          <LlmTraceBlock title="Output" messages={llmLog?.output} />
        </div>
      )}
    </div>
  );
}

function LlmRequestDetailWrapper() {
  return (
    <ConfirmationProvider>
      <LlmLogApiProvider>
        <LlmRequestDetail />
      </LlmLogApiProvider>
    </ConfirmationProvider>
  );
}

export default LlmRequestDetailWrapper;
